import apis from '@apis/index';
import { Button, Image, useControllableState } from '@chakra-ui/react';
import ItemList from '@components/data-list/item-list';
import Content from '@components/content';
import MoaInfoText from '@components/moa-info-text';
import QuantityControl from '@components/quantity-control';
import useStore from '@hooks/useStoreMove';
import useCart from '@hooks/useCart';
import useLoader from '@hooks/useLoader';
import useOrder from '@hooks/useOrder';
import usePopup from '@hooks/usePopup';
import { addressSelector } from '@stores/address';
import { ICartMenus } from 'interfaces/cart';
import { useSession } from 'next-auth/react';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { AiOutlineClockCircle, AiOutlineClose } from 'react-icons/ai';
import { useSelector } from 'react-redux';
import {
  calculateMOA,
  replaceDeliveryTime,
  setComma,
  setCurrency,
} from 'utils/common';
import RecommendMenu from './components/recommend-menu';
import SignInDrawer from './popups/sign-in.drawer';

type TCartContainerProps = {
  next: () => void;
};

export const fetchBusinessTime = async () => {
  try {
    const { data } = await apis.coreConfig.checkBusinessStatus();
    if (data.code !== 200) throw new Error('Not code 200');

    return data;
  } catch (e) {
    console.log(e);
  }
};

export default function CartContainer({ next }: TCartContainerProps) {
  const { cart: cartQueryData, deleteMenu, controlMenuQuantity } = useCart();
  const { currentAddress } = useSelector(addressSelector);
  const cartData = cartQueryData!;
  const { promotionDiscount, finalAmount, hotdealAmount, promotionConstant } =
    useOrder();
  const { closePopup } = usePopup();
  const router = useRouter();
  const { t } = useTranslation('store');
  const isMain = useMemo(() => router.pathname === '/', []);
  const storeHook = isMain ? useStore() : undefined;
  const { progressLoader, stopLoader } = useLoader();
  const { openAlert, openPromisePopup } = usePopup();
  const { status } = useSession();

  const handleQuantityControl = (quantity: number, id: number) => {
    if (!cartData.menus) return;
    controlMenuQuantity(id, quantity);
  };

  const getTotalMarginPrice = () => {
    if (!cartData?.menus) return 0;
    const totalPrice = cartData?.menus
      .map(
        (menu) =>
          (menu.originalPrice +
            menu.options.reduce(
              (acc, option) => (acc += option.marginPrice),
              0,
            )) *
          (menu.quantity || 1),
      )
      .reduce((acc, menu) => (acc += menu), 0);
    return totalPrice;
  };

  const handleMenuRemove = (item: ICartMenus) => {
    deleteMenu(item.id);
  };

  const onOrderValid = async () => {
    const verifyCartData = cartData;
    if (
      !verifyCartData ||
      !verifyCartData.menus ||
      !Array.isArray(verifyCartData.menus)
    ) {
      return;
    }
    if (!cartData || !currentAddress || !verifyCartData.store) return;

    const menus = verifyCartData.menus;
    if (menus.length === 0) {
      await openAlert({
        description: (
          <>
            <p>{t('empty_cart_message')}</p>
          </>
        ),
        isConfirmButtonShow: false,
      });
      return;
    }

    const [foodAmount, liquorAmount] = menus.reduce(
      (acc, menu) => {
        if (menu.is_liquor) {
          acc[1] += +menu.price * +menu.quantity;
        } else {
          acc[0] += +menu.price * +menu.quantity;
        }

        return acc;
      },
      [0, 0] as [number, number],
    );
    if (liquorAmount > foodAmount) {
      await openAlert({
        description: (
          <>
            <p>{t('alcohol_food_price_warning')}</p>
          </>
        ),
        isConfirmButtonShow: false,
      });

      return;
    }

    progressLoader();

    const [isBizTime] = await Promise.all([fetchBusinessTime()]);

    stopLoader();
    if (!isBizTime?.data) {
      return;
    }

    if (status === 'unauthenticated') {
      const isSignIn = await openPromisePopup<'promiseDrawer', boolean>({
        isOpen: true,
        Component: SignInDrawer,
        props: {},
        type: 'promiseDrawer',
        popupOptions: { placement: 'bottom', size: 'md' },
      });

      if (!isSignIn) return;
    }
    next();
  };

  const handleStoreMove = isMain
    ? () => {
        if (storeHook && cartData.store) {
          storeHook.moveRestaurant({
            item: cartData.store,
            category: cartData.store.category,
            queryString: {
              lat: currentAddress?.lat || '',
              lng: currentAddress?.lng || '',
            },
          });
        }
        closePopup();
      }
    : undefined;

  const handleAddMenu = () => {
    if (isMain && handleStoreMove) {
      handleStoreMove();
    } else {
      closePopup();
    }
  };

  const productPrice = getTotalMarginPrice();
  const deliveryFee =
    (cartData?.store?.adjusted_delivery_fee || 0) +
    (cartData?.store?.addrResFee || 0);

  const moa = calculateMOA({
    moa: cartData?.store?.min_order_amount ?? 0,
    discounts: cartData?.store?.discounts,
  });

  return (
    <Content className="mt-[12px]">
      {cartData && cartData.store && (
        <>
          <div className="pb-[120px]">
            <div className={`mt-[12px] relative`}>
              <div className="flex gap-[6px]" onClick={handleStoreMove}>
                <p className="font-bold">
                  {cartData.store.translate_name || cartData.store.name}
                </p>
                <div className="flex items-center text-[12px]">
                  <AiOutlineClockCircle />
                  <span>
                    {t('estimated_delivery_time', {
                      time: replaceDeliveryTime(
                        cartData.store.estimated_delivery_time,
                        '' + t('order:min'),
                      ),
                    })}
                  </span>
                </div>
              </div>
            </div>
            <div className="pt-[30px]">
              <ul>
                <ItemList
                  items={cartData.menus ?? []}
                  renderer={(item, index) => (
                    <Renderer
                      item={item}
                      key={`${item.id}${index}`}
                      handleQuantityControl={handleQuantityControl}
                      handleMenuRemove={handleMenuRemove}
                    />
                  )}
                />
                <li className="bt bb py-[6px] text-center text-sm text-primary-800">
                  <p className="cursor-pointer" onClick={handleAddMenu}>
                    {t('add_menu_item')}
                  </p>
                </li>
              </ul>
            </div>
            <div className="py-[12px]">
              <RecommendMenu restaurant={cartData.store} />
            </div>
            <div className="bt">
              <div className="py-[20px]">
                <dl className="flex justify-between items-center">
                  <dt>{t('price_label')}</dt>
                  <dd>{setCurrency('kr', setComma(productPrice || 0))}</dd>
                </dl>
                <dl className="flex justify-between items-center">
                  <dt>{t('total_delivery_fee')}</dt>
                  <dd>{setCurrency('kr', setComma(deliveryFee))}</dd>
                </dl>
                {hotdealAmount > 0 && (
                  <dl className="flex justify-between items-center">
                    <dt className="flex items-center gap-[4px] font-bold">
                      <p className="flex items-center gap-[6px]">
                        {t('hot_deals_title')}
                      </p>
                      <object
                        data="/static/icons/fire-svgrepo-com.svg"
                        className="h-[18px] w-[18px]"
                      />
                    </dt>
                    <dd className="text-primary-700 font-bold">
                      <span>-{setCurrency('kr', setComma(hotdealAmount))}</span>
                    </dd>
                  </dl>
                )}
                {promotionDiscount > 0 && (
                  <dl className="flex justify-between items-center">
                    <dt className="font-bold">Promotion Event</dt>
                    <dd className="text-primary-700 font-bold">
                      -{setCurrency('kr', setComma(promotionDiscount))}
                    </dd>
                  </dl>
                )}
                {promotionConstant > 0 && (
                  <dl className="flex justify-between items-center">
                    <dt className="font-bold flex items-center">
                      {t('panther:instant_discount')}{' '}
                      <object
                        data="/static/icons/fire-svgrepo-com.svg"
                        className="h-[18px] w-[18px]"
                      />
                    </dt>
                    <dd className="text-primary-700 font-bold">
                      -{setCurrency('kr', setComma(promotionConstant))}
                    </dd>
                  </dl>
                )}
              </div>
              <dl className="flex justify-between items-center bt pt-[20px] mb-[12px]">
                <dt className="font-bold">{t('total_order_price_label')}</dt>
                <dd className="font-bold">
                  {setCurrency('kr', setComma(finalAmount))}
                </dd>
              </dl>
            </div>
          </div>
          <div className="fixed bottom-0 left-0 w-full py-[16px] bg-white border-t-[1px] border-solid border-[#D1D5DB] z-[50]">
            <MoaInfoText currentPrice={productPrice} moa={moa} />
            <Content>
              <Button
                className="bg-primary-700 w-full text-white"
                onClick={onOrderValid}
                isDisabled={productPrice < moa}
              >
                {productPrice < moa ? (
                  <Trans
                    i18nKey="store:minimum_order_amount_for_order"
                    components={{ span: <span className="font-bold" /> }}
                    values={{ amount: setComma(moa) + 'Won' }}
                  />
                ) : (
                  <p className="flex w-full relative items-center justify-center gap-[6px]">
                    {t('proceed_to_checkout')}
                  </p>
                )}
              </Button>
            </Content>
          </div>
        </>
      )}
    </Content>
  );
}

const Renderer = ({
  item,
  handleQuantityControl,
  handleMenuRemove,
}: {
  item: ICartMenus;
  handleQuantityControl: (quantity: number, id: number) => void;
  handleMenuRemove: (item: ICartMenus) => void;
}) => {
  const [value, setValue] = useControllableState({
    defaultValue: item.quantity,
  });

  const originPrice =
    (item.originalPrice +
      item.options.reduce((acc, option) => {
        acc += option.marginPrice;
        return acc;
      }, 0)) *
    item.quantity;
  const isDiscount =
    item.isHotdeal || (item.promotionPrice && item.promotionPrice > 0);

  return (
    <li
      key={item.id + item.name + item.options.length}
      className="py-[15px] bt"
    >
      <div className="flex gap-[12px]">
        {item.image && (
          <Image src={item.image} width="100px" objectFit="cover" />
        )}
        <div className="flex-1 relative">
          <div>
            <p>{(item.translate_name || item.name).trim()}</p>
            <p
              className={` ${
                isDiscount
                  ? 'line-through text-sm text-secondary-500 text-[14px]'
                  : 'text-[16px] font-bold'
              }`}
            >
              {setCurrency('kr', setComma(originPrice))}
            </p>
            {isDiscount && (
              <p className="text-[16px] font-bold">
                {setCurrency(
                  'kr',
                  setComma(
                    originPrice -
                      (item.isHotdeal ? 1000 * item.quantity : 0) -
                      (item.promotionPrice && item.promotionPrice > 0
                        ? item.promotionPrice * item.quantity
                        : 0),
                  ),
                )}
              </p>
            )}
            {item.options && item.options.length > 0 && (
              <p className="text-[12px] ml-[4px] text-secondary-400">
                {item.options
                  .map((option) => option.translate_name || option.name)
                  .join(', ')}
              </p>
            )}
            <div className="absolute top-0 right-0">
              <button onClick={() => handleMenuRemove(item)}>
                <AiOutlineClose />
              </button>
            </div>
          </div>
          <div className="flex items-center justify-end mt-[6px]">
            <QuantityControl
              value={value}
              handleSetValue={(_value) => {
                if (_value < 1) return;
                setValue(_value);
                handleQuantityControl(_value, item.id);
              }}
              max={item?.stock_amount}
            />
          </div>
        </div>
      </div>
    </li>
  );
};
